import { onDateInputChange, onDateInputKeyDown } from '@ui-common/utils/date'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { FormTextInputProps } from '@ui-components/global/ComponentProps'
import * as React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

export interface DateInputFieldProps<T extends object> extends Omit<FormTextInputProps, 'name'> {
  fieldName: keyof T
  onChangeSplitValue?: (splitValue: string[]) => void
  afterChange?: (newValue: string) => void
  shouldUnregister?: boolean
}

// Generic Date input field that supports adding dates in format MM/YYYY
// Note that you must wrap the component in <FormProvider>
export const DateInputField = <T extends object>(props: DateInputFieldProps<T>): React.ReactNode => {
  const intl = useIntl()
  const { fieldName, children, afterChange, onChangeSplitValue, shouldUnregister, ...rest } = props

  const dateField = String(fieldName)

  const methods = useFormContext()
  const { register, watch, setValue } = methods
  const selectedValue = watch(dateField)

  return (
    <FormTextInput
      maxLength={9}
      inputMode="numeric"
      placeholder={intl.formatMessage({ id: 'common-month-year-placeholder' })}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onDateInputKeyDown(event, (value) => {
          setValue(dateField, value)
        })
      }
      {...register(dateField, {
        shouldUnregister,
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          onDateInputChange(
            event,
            selectedValue,
            (value) => {
              setValue(dateField, value)
            },
            onChangeSplitValue,
          )
          if (afterChange) {
            afterChange(event.target.value)
          }
        },
      })}
      {...rest}
    >
      {children}
    </FormTextInput>
  )
}
